import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { API, post } from "modules/api";
import { AnswersMap } from "modules/workflow/types/Answer";
import { PublicCompleteData } from "./PublicMedicaidLeadInterview";
import PublicMedicaidEmailStep from "./PublicMedicaidEmailStep";
import PublicMedicaidInterviewStep from "./PublicMedicaidInterviewStep";
import styles from "./styles.module.scss";

type Step = "email_capture" | "interview" | "load_error" | "confirmation";

const cardStyle: React.CSSProperties = {
  backgroundColor: "#ffffff",
  borderRadius: "12px",
  padding: "40px",
  maxWidth: "480px",
  width: "100%",
  boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
  margin: "0 auto",
};

const PublicMedicaidPage: React.FC = () => {
  const { search } = useLocation();
  const partnerId = new URLSearchParams(search).get("partnerId");

  const [step, setStep] = useState<Step>("email_capture");
  const [jurisdiction, setJurisdiction] = useState("");
  const [publicLeadId, setPublicLeadId] = useState<string | null>(null);
  const [entryError, setEntryError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const prev = document.body.style.backgroundColor;
    document.body.style.backgroundColor = "#EBEBEB";
    return () => {
      document.body.style.backgroundColor = prev;
    };
  }, []);

  const handleEmailSubmit = async (values: AnswersMap) => {
    setEntryError("");
    setIsSubmitting(true);
    try {
      const { data } = await post<{ publicLeadId: string }>(
        API.PUBLIC_MEDICAID_ENTRY(),
        {
          email: values.email,
          jurisdiction: values.jurisdiction,
          partnerId: partnerId?.toUpperCase(),
        },
      );
      setJurisdiction(values.jurisdiction as string);
      setPublicLeadId(data.publicLeadId);
      setStep("interview");
    } catch (err: any) {
      const code = err?.response?.data?.short ?? "";
      if (code === "partner-not-found") {
        setEntryError(
          "This assessment link is not valid. Please contact your advisor for the correct link.",
        );
      } else if (code === "partner-medicaid-disabled") {
        setEntryError(
          "Medicaid services are not available for this partner. Please contact your advisor.",
        );
      } else if (code === "public-account-already-exists") {
        setEntryError(
          "Your account already exists. Please sign in to continue.",
        );
      } else {
        setEntryError(
          "There was an error starting your assessment. Please try again.",
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInterviewComplete = (_data: PublicCompleteData) => {
    setStep("confirmation");
  };

  const handleInterviewLoadError = () => {
    setStep("load_error");
  };

  const isCardStep =
    !partnerId ||
    step === "email_capture" ||
    step === "load_error" ||
    step === "confirmation";

  if (isCardStep) {
    return (
      <div className={styles["container--centered"]}>
        {!partnerId && (
          <div style={cardStyle}>
            <p style={{ color: "#dc2626", margin: 0 }}>
              This link is not valid. Please contact your advisor for the
              correct assessment link.
            </p>
          </div>
        )}

        {partnerId && step === "email_capture" && (
          <>
            <PublicMedicaidEmailStep
              isSubmitting={isSubmitting}
              error={entryError}
              onSubmit={handleEmailSubmit}
            />
          </>
        )}

        {partnerId && step === "load_error" && (
          <div style={cardStyle}>
            <p style={{ color: "#dc2626", margin: 0 }}>
              Unable to load the assessment. Please refresh the page and try
              again.
            </p>
          </div>
        )}

        {partnerId && step === "confirmation" && (
          <div style={cardStyle}>
            <h2 style={{ fontSize: 22, fontWeight: 700, margin: "0 0 16px" }}>
              Thank you for completing your Medicaid Qualification Assessment.
            </h2>
            <p
              style={{
                fontSize: 15,
                color: "#374151",
                margin: 0,
                lineHeight: 1.6,
              }}
            >
              Your report has been delivered to your email.
            </p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.main}>
        {partnerId && step === "interview" && publicLeadId && (
          <PublicMedicaidInterviewStep
            publicLeadId={publicLeadId}
            jurisdiction={jurisdiction}
            onComplete={handleInterviewComplete}
            onError={handleInterviewLoadError}
          />
        )}
      </div>
    </div>
  );
};

export default PublicMedicaidPage;
